import React, { useState, useEffect, useMemo } from 'react'
import { chain, insert, remove, map, last, uniq } from 'ramda'
import { Autosuggest } from '@/components/Autosuggest'

type SpecialtiesPickerProps = {
  label: string
  name: string
  value: string[]
  kind?: string
  onChange: (x: { name: string; value: string[] }) => void
  autosuggestOnFocus?: boolean
  hideAddAnother?: boolean
  style?: any
  placeholder?: string
}

type SpecialtyPickerProps = {
  label: string
  name: string
  value: string
  kind?: string
  onChange: (x: { name: string; value: string }) => void
  autosuggestOnFocus?: boolean
  style?: any
  placeholder?: string
}

const makeCategoryOption = (cats: { id: string; label: string }[]) => {
  const label = map(x => x.label, cats).join(' - ')
  const { id } = last(cats)
  return { id, label }
}

export const SpecialtiesPicker = ({
  label,
  name,
  value,
  kind,
  autosuggestOnFocus = true,
  onChange,
  hideAddAnother,
  style,
  placeholder,
}: SpecialtiesPickerProps) => {
  value = uniq(value)
  if (value.length === 0) {
    value = ['']
  }

  const handleChange = (i, { value: category_id }) => {
    onChange({ name, value: uniq(insert(i, category_id, remove(i, 1, value))) })
  }
  const addOne = () => {
    onChange({ name, value: uniq([].concat(value).concat([''])) })
  }

  return (
    <>
      {value.map((category_id, i) => (
        <React.Fragment key={`${category_id}-${i}`}>
          <SpecialtyPicker
            key={`${category_id}-${i}`}
            label={i > 0 ? 'I am also interested in…' : label}
            name={`${name}[]`}
            value={category_id}
            kind={kind}
            onChange={x => handleChange(i, x)}
            autosuggestOnFocus={autosuggestOnFocus}
            style={style}
            placeholder={placeholder}
          />
        </React.Fragment>
      ))}
      {value.findIndex(x => x == '') > -1 || hideAddAnother ? null : (
        <button type="button" onClick={addOne} className="fake-link">
          Add another specialty
        </button>
      )}
    </>
  )
}

export const SpecialtyPicker = ({
  label,
  name,
  value,
  kind,
  autosuggestOnFocus = true,
  onChange,
  style,
  placeholder,
}: SpecialtyPickerProps) => {
  const categories =
    typeof window === 'undefined' ? [] : (window['medshrCategories'] ?? [])

  const showField = false

  const specialtyOptions = useMemo(
    () =>
      chain(
        field =>
          chain(
            specialty =>
              [
                makeCategoryOption(
                  showField ? [field, specialty] : [specialty]
                ),
              ].concat(
                map(
                  subspec =>
                    makeCategoryOption(
                      showField
                        ? [field, specialty, subspec]
                        : [specialty, subspec]
                    ),
                  specialty.children
                )
              ),
            field.children
          ),
        categories.specialties || []
      ),
    [categories.specialties]
  )

  const [term, setTerm] = useState('')
  const [initial, setInitial] = useState(() =>
    specialtyOptions.find(x => x.id === value)
  )
  const [selection, setSelection] = useState(
    () => specialtyOptions.find(x => x.id === value)?.label ?? ''
  )
  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    const lterm = term.toLowerCase()
    setSuggestions(
      specialtyOptions.filter(
        x =>
          (kind !== 'dentist' || x.id.startsWith('dentistry')) &&
          x.label.toLowerCase().indexOf(lterm) > -1
      )
    )
  }, [term])

  const handleChangeEvent = (e, { newValue }) => {}

  const handleSuggestion = suggestion => {
    /* if (method === 'enter') { */
    /*   e.preventDefault() */
    /* } */
    if (suggestion) {
      if (selection !== suggestion.label) {
        setSelection(suggestion.label)
        setTerm('')
        onChange({ name, value: suggestion.id })
      }
    } else if (selection) {
      setSelection('')
      setTerm('')
      onChange({ name, value: '' })
    }
  }

  const handleSuggestionsUpdateRequested = value => {
    setTerm(value ?? '')
  }

  const getSuggestionValue = (suggestion: any): string => {
    return suggestion?.label ?? ''
  }

  const renderSuggestion = (suggestion: any): React.ReactElement => {
    return (
      <div>
        <div>{suggestion.label}</div>
      </div>
    )
  }

  return (
    <>
      <input type="hidden" name={name} value={value} />
      <Autosuggest
        label={label}
        suggestions={
          value ? [{ id: '', label: 'All' }].concat(suggestions) : suggestions
        }
        onSuggestionsFetchRequested={handleSuggestionsUpdateRequested}
        onSuggestionSelected={handleSuggestion}
        getSuggestionValue={getSuggestionValue}
        placeholder={selection || placeholder}
        renderSuggestion={renderSuggestion}
        initialSelectedItem={initial}
      />
    </>
  )
}

export default SpecialtyPicker
